import {z} from 'zod';

const appEnv = {
  API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  AGENT_ROUTE_PREFIX: import.meta.env.VITE_AGENT_ROUTE_PREFIX,
  APP_MUI_X_LICENSE_KEY: import.meta.env.VITE_APP_MUI_X_LICENSE_KEY,
  APP_PROFILE: import.meta.env.VITE_APP_PROFILE,
  DIRECT_AUTH_ENABLED: import.meta.env.VITE_DIRECT_AUTH_ENABLED,
  AGENT_DIRECT_URL: import.meta.env.VITE_AGENT_DIRECT_URL,
  DIRECT_AUTH_IDLE_TIMEOUT_MIN: import.meta.env.VITE_DIRECT_AUTH_IDLE_TIMEOUT_MIN,
};

const profileEnum = ['dev', 'prod'] as const;
export type AppProfile = (typeof profileEnum)[number];

const isDev = appEnv.APP_PROFILE === 'dev';
const defaultBaseUrl =
  appEnv.API_BASE_URL ??
  (isDev ? 'http://localhost:9000' : window.location.origin);
export const envSchema = z.object({
  API_BASE_URL: z
    .string()
    .min(1, 'API Base URL is required')
    // .url('API Base URL must be a valid URL')
    .default(defaultBaseUrl),
  
  AGENT_ROUTE_PREFIX: z
    .string()
    .min(1, 'AGENT ROUTE PREFIX is required'),

  APP_MUI_X_LICENSE_KEY: z
    .string()
    .min(1, 'MUI X License key is required')
    .optional()
    .default('PUT_YOUR_KEY_HERE'),

  APP_PROFILE: z
    .enum(profileEnum, {
      description: 'Profile must be one of: dev, prod',
    })
    .optional()
    .default('dev')
    .transform(val => val),

  // Direct browser-signed auth (this.i wqz7k3mnbp).
  // Master on/off flag; off by default so the app behaves as the gateway path.
  DIRECT_AUTH_ENABLED: z
    .union([z.boolean(), z.string()])
    .optional()
    .default(false)
    .transform(v => v === true || v === 'true'),

  // Agent base URL the browser calls directly when DIRECT_AUTH_ENABLED is on
  // (wiring lands in a later slice). Empty when unused.
  AGENT_DIRECT_URL: z.string().optional().default(''),

  // Minutes of user inactivity after which the in-memory session is cleared.
  DIRECT_AUTH_IDLE_TIMEOUT_MIN: z.preprocess(
    v => (v === undefined || v === '' ? undefined : Number(v)),
    z.number().int().positive().optional().default(10),
  ),

}).superRefine((env, ctx) => {
  // Direct-auth needs a concrete agent URL to call. If the flag is flipped on
  // while AGENT_DIRECT_URL is left at its empty default, constant.ts would
  // silently fall back to the gateway base URL — "direct auth" that still
  // routes through the gateway. Fail fast at boot rather than confuse the
  // login/probe flow.
  if (env.DIRECT_AUTH_ENABLED && !isHttpUrl(env.AGENT_DIRECT_URL)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['AGENT_DIRECT_URL'],
      message:
        'AGENT_DIRECT_URL must be a valid http(s) URL when DIRECT_AUTH_ENABLED is true',
    });
  }
});

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export type EnvSchema = z.infer<typeof envSchema>;

export function validateEnv(): EnvSchema {
  try {
    const env = envSchema.parse(appEnv);

    if (env.APP_PROFILE === 'prod') {
      // Enforce MUI X license key format in production
      if (
        env.APP_MUI_X_LICENSE_KEY === '' ||
        env.APP_MUI_X_LICENSE_KEY === 'PUT_YOUR_KEY_HERE'
      ) {
        throw new Error('MUI X License key is required in production');
      }
    }

    return env;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors
        .map(err => {
          const field = err.path.join('.');
          return `${field}: ${err.message}`;
        })
        .join('\n');

      throw new Error(`Environment validation failed:\n${errors}`);
    }
    throw error;
  }
}

let env: EnvSchema;
export function getEnv(): EnvSchema {
  if (!env) {
    env = validateEnv();
console.log('Environment variables validated successfully:', env);
    // Freeze the object in development to prevent modifications
    if (env.APP_PROFILE === 'dev') {
      Object.freeze(env);
    }
  }
  return env;
}
